import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Globe,
  Plus,
  RefreshCw,
  Link2,
  Settings,
  Zap,
  DollarSign,
  CheckCircle,
  XCircle,
  ExternalLink,
  Trash2,
  Play,
  Building2,
} from 'lucide-react';

export default function DSPPartnersPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [syncLoading, setSyncLoading] = useState({});
  const [formData, setFormData] = useState({
    partner_name: '',
    api_endpoint: '',
    api_key: '',
    api_secret: '',
    publisher_id: '',
    is_active: true,
    sync_inventory: true,
    min_floor_price: 0.05,
    supported_formats: ['image', 'video'],
    settings: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [partnersRes, availableRes] = await Promise.all([
        api.get('/dsp-partners'),
        api.get('/dsp-partners/available'),
      ]);
      setPartners(partnersRes.data || []);
      setAvailablePartners(availableRes.data.partners || []);
    } catch (error) {
      toast.error('Failed to load DSP partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPartnerType = (partner) => {
    setFormData({
      ...formData,
      partner_name: partner.id,
      api_endpoint: getDefaultEndpoint(partner.id),
    });
  };

  const getDefaultEndpoint = (partnerId) => {
    const endpoints = {
      lemmatv: 'https://api.lemmatechnologies.com/v1',
      hivestack: 'https://api.hivestack.com/v2',
      vistar: 'https://api.vistarmedia.com/v1',
      place_exchange: 'https://api.placeexchange.com/v1',
      broadsign: 'https://api.broadsign.com/reach/v1',
    };
    return endpoints[partnerId] || '';
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    try {
      await api.post('/dsp-partners', formData);
      toast.success('DSP partner added successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to add DSP partner');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Are you sure you want to remove this DSP connection?')) return;
    try {
      await api.delete(`/dsp-partners/${partnerId}`);
      toast.success('DSP partner removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove DSP partner');
    }
  };

  const handleSyncInventory = async (partnerId) => {
    setSyncLoading({ ...syncLoading, [partnerId]: true });
    try {
      const response = await api.post(`/dsp-partners/${partnerId}/sync-inventory`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to sync inventory');
    } finally {
      setSyncLoading({ ...syncLoading, [partnerId]: false });
    }
  };

  const resetForm = () => {
    setFormData({
      partner_name: '',
      api_endpoint: '',
      api_key: '',
      api_secret: '',
      publisher_id: '',
      is_active: true,
      sync_inventory: true,
      min_floor_price: 0.05,
      supported_formats: ['image', 'video'],
      settings: {},
    });
  };

  const getPartnerInfo = (partnerName) => {
    return availablePartners.find((p) => p.id === partnerName) || {};
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dsp-partners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            DSP Partner Integrations
          </h1>
          <p className="text-zinc-400 mt-1">
            Connect with demand-side platforms to sell inventory programmatically
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-500"
          data-testid="add-partner-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add DSP Partner
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Link2 className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{partners.length}</p>
                <p className="text-sm text-zinc-400">Connected DSPs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {partners.filter((p) => p.is_active).length}
                </p>
                <p className="text-sm text-zinc-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{availablePartners.length}</p>
                <p className="text-sm text-zinc-400">Available DSPs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="connected">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="connected" className="data-[state=active]:bg-blue-600">
            <Link2 className="w-4 h-4 mr-2" />
            Connected ({partners.length})
          </TabsTrigger>
          <TabsTrigger value="available" className="data-[state=active]:bg-blue-600">
            <Globe className="w-4 h-4 mr-2" />
            Available DSPs
          </TabsTrigger>
        </TabsList>

        {/* Connected Partners */}
        <TabsContent value="connected" className="mt-6">
          {partners.length === 0 ? (
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-12 text-center">
                <Link2 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-xl font-semibold text-white mb-2">No DSP Partners Connected</h3>
                <p className="text-zinc-400 mb-4">Connect with a DSP partner to start selling inventory programmatically</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First DSP
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {partners.map((partner) => {
                const info = getPartnerInfo(partner.partner_name);
                return (
                  <Card key={partner.id} className="glass-card border-zinc-800">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">{info.name || partner.partner_name}</h3>
                            <p className="text-zinc-500 text-sm">{partner.api_endpoint}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={
                          partner.is_active
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                        }>
                          {partner.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-500">Publisher ID</p>
                          <p className="text-white">{partner.publisher_id || 'Not set'}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Floor Price</p>
                          <p className="text-white">${partner.min_floor_price}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Last Sync</p>
                          <p className="text-white">
                            {partner.last_sync
                              ? new Date(partner.last_sync).toLocaleDateString()
                              : 'Never'}
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Formats</p>
                          <p className="text-white">{partner.supported_formats?.join(', ')}</p>
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncInventory(partner.id)}
                          disabled={syncLoading[partner.id]}
                          className="border-zinc-700 flex-1"
                        >
                          {syncLoading[partner.id] ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Sync Inventory
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePartner(partner.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Available Partners */}
        <TabsContent value="available" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availablePartners.map((partner) => (
              <Card key={partner.id} className="glass-card border-zinc-800 card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                      <Globe className="w-6 h-6 text-zinc-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{partner.name}</h3>
                      <p className="text-zinc-500 text-xs">{partner.regions?.join(', ')}</p>
                    </div>
                  </div>

                  <p className="text-zinc-400 text-sm mb-4">{partner.description}</p>

                  <div className="flex gap-1 flex-wrap mb-4">
                    {partner.supported_formats?.map((format) => (
                      <Badge key={format} variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                        {format}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(partner.website, '_blank')}
                      className="border-zinc-700 flex-1"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Website
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        handleSelectPartnerType(partner);
                        setDialogOpen(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-500 flex-1"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Connect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Partner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Add DSP Partner
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePartner} className="space-y-4">
            <div>
              <Label className="text-zinc-300">DSP Partner</Label>
              <Select
                value={formData.partner_name}
                onValueChange={(value) => {
                  setFormData({
                    ...formData,
                    partner_name: value,
                    api_endpoint: getDefaultEndpoint(value),
                  });
                }}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select DSP" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {availablePartners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-300">API Endpoint</Label>
              <Input
                value={formData.api_endpoint}
                onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="https://api.dsp.com/v1"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-300">API Key</Label>
              <Input
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                type="password"
                placeholder="Your API key"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-300">Publisher ID</Label>
              <Input
                value={formData.publisher_id}
                onChange={(e) => setFormData({ ...formData, publisher_id: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Your publisher/network ID"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Minimum Floor Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.min_floor_price}
                onChange={(e) => setFormData({ ...formData, min_floor_price: parseFloat(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label className="text-zinc-300">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.sync_inventory}
                  onCheckedChange={(checked) => setFormData({ ...formData, sync_inventory: checked })}
                />
                <Label className="text-zinc-300">Auto-sync inventory</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                Add Partner
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
